import React, { useCallback } from "react";
import styled from "@emotion/styled";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  Typography,
  CircularProgress,
  Button,
  Stack,
  useTheme,
} from "@mui/material";
import ReactJson from "react-json-view";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Article as ArticleIcon,
  ExpandMore as ExpandMoreIcon,
  RemoveRedEye as RemoveRedEyeIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";

import { Download as DownloadIcon } from "react-feather";
import BackIcon from "@mui/icons-material/ArrowBackOutlined";
import ReactCountryFlag from "react-country-flag";

import useLLMEvent from "../../../hooks/user/useLLMEvent";
import useAuth from "../../../hooks/useAuth";
import { formatDate } from "../../../utils/format";

import { SnackbarAlert } from "../../../components/pages/common/styled";
import {
  downloadAsPdf,
  downloadObjectAsJson,
  getActionStatusString,
  isReqBlockedBySD,
  KeyValueComponent,
  TabPanel,
  TitleComponent,
} from "../../../components/pages/application/analytics/common";

import { ReactComponent as TimelineIcon } from "../../../vendor/button/timeline.svg";
import { ReactComponent as RawdataIcon } from "../../../vendor/button/raw_data.svg";
import Speedometer from "../../../components/pages/application/analytics/Speedometer";
import { NGXFirewallAction } from "../../../utils/constants";

const Root = styled.div`
  justify-content: center;
  align-items: center;
  display: flex;
`;

function LLMAnalyticsFirewallEventsDetail() {
  const theme = useTheme();

  const navigate = useNavigate();
  const { eventID } = useParams();
  const { isAuthenticated } = useAuth();
  const { getFwEvent, fwEvent, errMsg, setErr } = useLLMEvent();
  const [request_headers, setRequestHeaders] = React.useState([]);
  const [response_headers, setResponseHeaders] = React.useState([]);

  const [tabIndex, setTabeIndex] = React.useState(0);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      getFwEvent(eventID);
    }
    return () => setErr(null);
  }, [isAuthenticated, setErr, getFwEvent, eventID]);

  const [snackOpen, setSnackOpen] = React.useState(false);
  const [message, setMessage] = React.useState();
  const [success, setSuccess] = React.useState();
  const handleSnackClose = () => {
    setSnackOpen(false);
    setErr(null);
  };

  React.useEffect(() => {
    if (errMsg) {
      setSnackOpen(true);
      setMessage(errMsg);
      setSuccess("error");
    } else {
      setSnackOpen(false);
      setMessage(null);
    }
  }, [errMsg]);

  React.useEffect(() => {
    var tmpHeader = [];
    if (fwEvent?.request_headers) {
      Object.keys(fwEvent?.request_headers).forEach(function (key) {
        tmpHeader.push({ key: key, value: fwEvent?.request_headers[key] });
      });
    }
    setRequestHeaders(tmpHeader);
    tmpHeader = [];
    if (fwEvent?.response_headers) {
      Object.keys(fwEvent?.response_headers).forEach(function (key) {
        tmpHeader.push({ key: key, value: fwEvent?.response_headers[key] });
      });
    }
    setResponseHeaders(tmpHeader);
  }, [fwEvent]);

  const onBackPressed = () => {
    // navigate("/llm/analytics/events");
    navigate(-1);
  };

  const downloadEventDetail = () => {
    const curDate = new Date();
    downloadAsPdf("eventDetail", `Layerd Signature WAF events (${formatDate(curDate)}).pdf`);
  };

  const handleDownloadClick = (e) => {
    e.preventDefault();
    if (0 === tabIndex) {
      downloadEventDetail();
    } else if (1 === tabIndex) {
      downloadObjectAsJson(fwEvent?.raw, "SG_RawEventData");
    }
  };

  const getStatusColor = useCallback(
    (action) => {
      switch (action) {
        case NGXFirewallAction.LOG:
          return theme.palette.custom.yellow.main;
        case NGXFirewallAction.ALLOW:
          return theme.palette.green.main;
        case NGXFirewallAction.BLOCK:
          return theme.palette.custom.red.main;
        default:
          return theme.palette.grey.main;
      }
    },
    [theme.palette]
  );

  const getStatusBackground = useCallback(
    (action) => {
      switch (action) {
        case NGXFirewallAction.LOG:
          return theme.palette.custom.yellow.opacity_50;
        case NGXFirewallAction.ALLOW:
          return theme.palette.custom.green.opacity_20;
        case NGXFirewallAction.BLOCK:
          return theme.palette.custom.red.opacity_10;
        default:
          return theme.palette.grey.main;
      }
    },
    [theme.palette]
  );

  const handleClickEditRule = (zoneID, rule_id) => {
    if (zoneID) {
      navigate(`/llm/${zoneID}/firewall/edit/${rule_id}`);
    }
  };

  return (
    <React.Fragment>
      <Helmet title="Firewall Event Detail" />
      <Grid container spacing={6} mt={9} sx={{ display: "flex", alignItems: "center" }}>
        <Grid item>
          <Typography variant="h1" gutterBottom display="inline">
            Firewall Event Detail
          </Typography>
        </Grid>
        <Grid item xs></Grid>
        <Grid item display="flex" alignItems="center">
          <Button variant="text" startIcon={<DownloadIcon />} onClick={handleDownloadClick}>
            Download
          </Button>
          <Button variant="contained" color="warning" size="ui" sx={{ marginLeft: 4 }} startIcon={<BackIcon />} onClick={onBackPressed}>
            Back
          </Button>
        </Grid>
      </Grid>
      {fwEvent === null ? (
        <>
          <Root mt={8}>
            <CircularProgress color="primary" />
          </Root>
        </>
      ) : (
        <>
          <Grid container mt={8} spacing={2}>
            <Grid item xs={6}>
              <Button
                variant={tabIndex === 1 ? "outlined" : "contained"}
                color="tab"
                onClick={() => setTabeIndex(0)}
                fullWidth
                sx={{ height: "106px", borderWidth: "0px", borderRadius: "8px", padding: 1 }}
              >
                <Grid container>
                  <Grid xs={12}>
                    <TimelineIcon />
                  </Grid>
                  <Grid xs={12}>
                    <Typography variant="h2" pl="8px" sx={{ fontSize: "15px" }}>
                      Timeline
                    </Typography>
                  </Grid>
                </Grid>
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant={tabIndex === 0 ? "outlined" : "contained"}
                color="tab"
                onClick={() => setTabeIndex(1)}
                fullWidth
                sx={{ height: "106px", borderWidth: "0px", borderRadius: "8px", padding: 1 }}
              >
                <Grid container>
                  <Grid xs={12}>
                    <RawdataIcon />
                  </Grid>
                  <Grid xs={12}>
                    <Typography variant="h2" pl="8px" sx={{ fontSize: "15px" }}>
                      Raw data
                    </Typography>
                  </Grid>
                </Grid>
              </Button>
            </Grid>
          </Grid>
          <TabPanel tabIndex={tabIndex} index={0} p={0}>
            <Stack id="eventDetail" pt={7} direction="column" spacing={2.5}>
              <Accordion defaultExpanded={true}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Grid container display="flex" alignItems="center">
                    <Grid item xs={3} display="flex" alignItems="center">
                      <ArticleIcon sx={{ mr: 4 }} />
                      <Typography variant="h2">Activity Log</Typography>
                    </Grid>
                    <Typography ml={4} variant="h3">
                      {fwEvent?.method}
                    </Typography>
                    <Typography ml={2} variant="h3">
                      {fwEvent?.uri}
                    </Typography>
                    <Grid item xs />
                    <Grid item pr={2}>
                      {fwEvent?.timestamp ? formatDate(fwEvent?.timestamp) : "-"}
                    </Grid>
                  </Grid>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container px={3.5} pb={4}>
                    <KeyValueComponent keyWidth={100} keyStr={"Host Name"} valueStr={fwEvent?.host_name} />
                    <Grid item xs={12} display="flex" alignItems={"center"} pl={4} mt={4}>
                      <Typography sx={{ width: "100px", wordBreak: "break-all", "text-transform": "capitalize" }}>Source IP</Typography>
                      <Box ml={3.5} display="flex" alignItems={"center"}>
                        <ReactCountryFlag countryCode={fwEvent?.country_iso_code} svg title={fwEvent?.country_name} />
                        <Typography ml={2}>{fwEvent?.src_ip || "-"}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Grid container display="flex" alignItems="center">
                    <Grid item xs={3} display="flex" alignItems="center">
                      <ArrowForwardIcon sx={{ mr: 4 }} />
                      <Typography variant="h2">Request</Typography>
                    </Grid>
                    <Typography ml={4} variant="h3">
                      <Stack
                        direction="row"
                        spacing={1.5}
                        p={2.5}
                        alignItems="center"
                        sx={{ background: getStatusBackground(fwEvent?.action), borderRadius: "8px" }}
                      >
                        <Box
                          style={{
                            width: 10,
                            height: 10,
                            backgroundColor: getStatusColor(fwEvent?.action),
                            borderRadius: 100,
                          }}
                        />
                        <Typography variant="textMedium">{getActionStatusString(fwEvent?.action)}</Typography>
                      </Stack>
                    </Typography>
                    <Grid item xs />
                  </Grid>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container px={3.5} pb={4}>
                    <KeyValueComponent keyStr={"Path"} valueStr={fwEvent?.uri} keyWidth={144} />
                    <KeyValueComponent keyStr={"Method"} valueStr={fwEvent?.method} keyWidth={144} />
                    <KeyValueComponent keyStr={"Host"} valueStr={fwEvent?.host_name} keyWidth={144} />
                    <KeyValueComponent keyStr={"User Agent"} valueStr={fwEvent?.ua} keyWidth={144} />
                    <TitleComponent title="Headers" />
                    {request_headers?.map((h) => {
                      return <KeyValueComponent keyStr={h?.key} valueStr={h?.value} keyWidth={144} />;
                    })}
                    <TitleComponent title="Query" />
                    {fwEvent?.query?.map((h) => {
                      return <KeyValueComponent keyStr={h?.key} valueStr={h?.value} keyWidth={144} />;
                    })}
                    <TitleComponent title="Form" />
                    {fwEvent?.formdata?.map((h) => {
                      return <KeyValueComponent keyStr={h?.key} valueStr={h?.value} keyWidth={144} />;
                    })}
                    {/* <TitleComponent title="Other" /> */}
                  </Grid>
                </AccordionDetails>
              </Accordion>
              <Accordion defaultExpanded={true}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <SecurityIcon sx={{ mr: 4 }} />
                  <Typography variant="h2">Firewall Detection</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container px={3.5} pb={4}>
                    <KeyValueComponent
                      keyStr={"Request Blocked"}
                      valueStr={isReqBlockedBySD(fwEvent?.resStatus) ? "true" : "false"}
                      width={144}
                    />
                    {fwEvent?.raw?.sd_firewall_rule && (
                      <>
                        <Grid item xs={12}>
                          <TitleComponent title="Matched Firewall Rule" />
                        </Grid>
                        <Grid item xs={9}>
                          <Grid container>
                            {!!fwEvent?.raw?.sd_firewall_rule?.rule_id && (
                              <KeyValueComponent
                                keyStr={"Rule Name"}
                                valueStr={
                                  (fwEvent?.raw?.sd_firewall_rule?.rule_name || "Untitled") +
                                  "(" +
                                  fwEvent?.raw?.sd_firewall_rule?.rule_id +
                                  ")"
                                }
                              />
                            )}
                          </Grid>
                        </Grid>
                        <Grid item xs={3}>
                          <Button
                            variant="outlined"
                            color="primary"
                            mr={4}
                            disabled={!fwEvent?.raw?.sd_firewall_rule?.rule_id}
                            onClick={(e) =>
                              handleClickEditRule(fwEvent?.raw?.sd_firewall_rule?.zone_id, fwEvent?.raw?.sd_firewall_rule?.rule_id)
                            }
                          >
                            Edit Firewall Rule
                          </Button>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center">
                    <ArrowBackIcon sx={{ mr: 4 }} />
                    <Typography variant="h2">Response</Typography>
                    <Typography pl={4}>{fwEvent?.resStatus}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container px={3.5} pb={4}>
                    <KeyValueComponent keyStr={"Response Status"} valueStr={fwEvent?.resStatus} />
                    <TitleComponent title="Headers" />
                    {response_headers?.map((h) => {
                      return <KeyValueComponent keyStr={h?.key} valueStr={h?.value} />;
                    })}
                  </Grid>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <RemoveRedEyeIcon sx={{ mr: 4 }} />
                  <Typography variant="h2">IP Reputation</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ py: 4 }}>
                  <Grid container>
                    <Grid item xs={12} md={6}>
                      <Grid container>
                        <Grid item xs={12} display="flex" alignItems={"center"} pl={4} pt={4}>
                          <Typography sx={{ width: "210px", wordBreak: "break-all", "text-transform": "capitalize" }}>
                            IP Address
                          </Typography>
                          {fwEvent?.abuseip?.countryCode && (
                            <Box ml={4} display="flex" alignItems={"center"}>
                              <ReactCountryFlag
                                countryCode={fwEvent?.abuseip?.countryCode}
                                svg
                                title={fwEvent?.abuseip?.countryName || fwEvent?.abuseip?.countryCode}
                              />
                              <Typography ml={2}>{fwEvent?.abuseip?.ipAddress || "-"}</Typography>
                            </Box>
                          )}
                        </Grid>
                        {undefined !== fwEvent?.abuseip?.abuseConfidenceScore && null !== fwEvent?.abuseip?.abuseConfidenceScore && (
                          <KeyValueComponent
                            keyStr={"Abuse Confidence Score"}
                            valueStr={fwEvent?.abuseip?.abuseConfidenceScore?.toString()}
                          />
                        )}
                        {fwEvent?.abuseip?.usageType && <KeyValueComponent keyStr={"Usage Type"} valueStr={fwEvent?.abuseip?.usageType} />}
                        {fwEvent?.abuseip?.isp && <KeyValueComponent keyStr={"ISP"} valueStr={fwEvent?.abuseip?.isp} />}
                        {undefined !== fwEvent?.abuseip?.totalReports && null !== fwEvent?.abuseip?.totalReports && (
                          <KeyValueComponent keyStr={"Total Reports"} valueStr={fwEvent?.abuseip?.totalReports?.toString()} />
                        )}

                        {fwEvent?.abuseip?.reports?.map((report, idx) => (
                          <>
                            <TitleComponent title={`Report ${idx + 1}`} />
                            {report.reportedAt && <KeyValueComponent keyStr={"Reported Date"} valueStr={formatDate(report.reportedAt)} />}
                            <KeyValueComponent keyStr={"Comment"} valueStr={report.comment} />
                            {0 < report.categories?.length && (
                              <KeyValueComponent keyStr={"Categories"} valueStr={report.categories.join()} />
                            )}
                            <Grid item xs={12} display="flex" alignItems={"center"}>
                              <Typography
                                sx={{ width: "210px", textAlign: "right", wordBreak: "break-all", "text-transform": "capitalize" }}
                              >
                                Reporter ID
                              </Typography>
                              {report?.reporterCountryCode && (
                                <Box ml={4} display="flex" alignItems={"center"}>
                                  <ReactCountryFlag
                                    countryCode={report.reporterCountryCode}
                                    svg
                                    title={report.reporterCountryName || report.reporterCountryCode}
                                  />
                                  <Typography ml={2}>{report.reporterId || "-"}</Typography>
                                </Box>
                              )}
                            </Grid>
                          </>
                        ))}
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={6} px={4}>
                      <Box
                        sx={{
                          width: "100%",
                          paddingY: "14px",
                          background: theme.palette.custom.white.bglight,
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Box m={"auto"} textAlign={"center"}>
                          <Typography variant="textSemiBold" pb={2}>
                            Abuse confident score
                          </Typography>
                          <Speedometer value={fwEvent?.abuseip?.abuseConfidenceScore ?? 0} />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Stack>
          </TabPanel>
          <TabPanel tabIndex={tabIndex} index={1}>
            <Box mt={7} px={4} py={7} sx={{ background: "white", borderRadius: 2 }}>
              <ReactJson name="raw" src={fwEvent?.raw} displayDataTypes={false} style={{ wordBreak: "break-word" }} />
            </Box>
          </TabPanel>
        </>
      )}
      <SnackbarAlert open={snackOpen} onClose={handleSnackClose} severity={success} message={message} />
    </React.Fragment>
  );
}
export default LLMAnalyticsFirewallEventsDetail;
